import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PackageSearch, Loader2 } from "lucide-react";

/**
 * ItemAutocomplete — an <Input> that fetches item suggestions from /api/items?q=
 * and shows a dropdown. Every time the parent record (invoice/MRN/hisaab) is saved,
 * the backend upserts the typed name into the master collection so past entries
 * come back as suggestions on the next typing session.
 *
 * Props:
 *   value: string
 *   onChange(name)             — for uncontrolled use, called with new name
 *   onPick(item)               — optional, called when user selects an existing item
 *                                receives {name, unit, last_rate, hsn, gst_pct}
 *   placeholder, testid, autoFocus
 */
export default function ItemAutocomplete({ value, onChange, onPick, placeholder, testid, className }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const skipNextFetch = useRef(false);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (!open) return;
    const term = q.trim();
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/items${term ? `?q=${encodeURIComponent(term)}` : ""}&limit=8`);
        setItems(data || []);
        setActive(0);
      } catch { setItems([]); } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (it) => {
    skipNextFetch.current = true;
    setQ(it.name);
    setOpen(false);
    onChange && onChange(it.name);
    onPick && onPick(it);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && items[active]) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={rootRef}>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); onChange && onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "Type item name — past items suggest karega"}
        data-testid={testid}
        className={className}
        autoComplete="off"
      />
      {open && (items.length > 0 || loading) && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/>Searching…</div>
          )}
          {!loading && items.map((it, i) => (
            <button
              type="button"
              key={it.id}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === active ? "bg-amber-50" : "hover:bg-slate-50"}`}
              data-testid={`${testid}-suggestion-${i}`}
            >
              <PackageSearch className="w-3.5 h-3.5 text-amber-600 shrink-0"/>
              <span className="flex-1 truncate">{it.name}</span>
              {it.last_rate > 0 && <span className="text-[10px] font-mono text-slate-500">₹{it.last_rate}/{it.unit || "Nos"}</span>}
              {it.usage_count > 1 && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5">×{it.usage_count}</span>}
            </button>
          ))}
          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">No past items — first time save karo, next time suggest hoga.</div>
          )}
        </div>
      )}
    </div>
  );
}
